import { z } from 'zod';

/**
 * The account vault (Sprint 2 KB, MOD-08; Figma C03.4): logins, brand files and links the team shares with a business.
 * A login's password is kept encrypted and is only ever sent back through the reveal call, never in a list.
 */

export const DELIVERABLE_KINDS = ['login', 'file', 'link'] as const;
export const deliverableKindSchema = z.enum(DELIVERABLE_KINDS);
export type DeliverableKind = z.infer<typeof deliverableKindSchema>;

/** The tile icons the vault knows how to draw (Phosphor names, kebab-case). */
export const DELIVERABLE_ICONS = [
  'globe',
  'map-pin',
  'envelope',
  'key',
  'image',
  'file-text',
  'link',
  'chart-bar',
] as const;
export const deliverableIconSchema = z.enum(DELIVERABLE_ICONS);
export type DeliverableIcon = z.infer<typeof deliverableIconSchema>;

/** A revealed login hides itself again after this long (Figma C03.4: "hides after 30 s"). */
export const REVEAL_SECONDS = 30;
/** The API answers a reveal with this code when the session has not confirmed its password lately. */
export const REAUTH_REQUIRED_CODE = 'REAUTH_REQUIRED';

export const FIELD_VALUE_MAX = 4000;
const text = (max: number) => z.string().trim().max(max);

export const fieldTypeSchema = z.enum(['text', 'link', 'password', 'file']);
export type DeliverableFieldType = z.infer<typeof fieldTypeSchema>;

/** A file the browser already put in the bucket through a presigned upload. */
export const vaultFileRefSchema = z.object({
  key: z.string().min(1).max(512),
  name: text(255).min(1),
  contentType: text(128).min(1),
  size: z.number().int().nonnegative(),
});

const httpUrl = (value: string) => /^https?:\/\/\S+$/i.test(value);

export const deliverableFieldInputSchema = z
  .object({
    /** Set when editing a field that already exists; leaving `value` out then keeps what is stored. */
    id: z.string().uuid().optional(),
    label: text(255).min(1, 'Name this field'),
    type: fieldTypeSchema,
    value: z.string().max(FIELD_VALUE_MAX).optional(),
    file: vaultFileRefSchema.optional(),
    /** Encrypt this value too. A password is always encrypted. */
    secret: z.boolean().default(false),
    visibleToClient: z.boolean().default(true),
  })
  .superRefine((field, ctx) => {
    if (field.type === 'file') {
      if (!field.file && !field.id) ctx.addIssue({ code: 'custom', path: ['file'], message: 'Add the file' });
      return;
    }
    if (!field.id && !field.value?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: 'Add a value' });
    }
    if (field.type === 'link' && field.value?.trim() && !httpUrl(field.value.trim())) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: 'Use a full link starting with https://' });
    }
  });
export type DeliverableFieldInput = z.infer<typeof deliverableFieldInputSchema>;

const deliverableBase = {
  title: text(255).min(1, 'Give it a name'),
  description: text(500).optional(),
  category: text(64).optional(),
  icon: deliverableIconSchema.optional(),
  /** The task this came out of, when there was one. The vault itself belongs to the business. */
  taskId: z.string().min(1).max(128).optional(),
  /** The project it was made for ("Link to" in Add to vault). `null` on an edit takes the link away. */
  projectId: z.string().min(1).max(128).nullable().optional(),
};

/** What each kind has to hold so the vault never shows an empty card; null when the field types are fine. */
export function deliverableShapeIssue(kind: DeliverableKind, types: DeliverableFieldType[]): string | null {
  if (kind === 'login') {
    if (!types.some((t) => t === 'password' || t === 'text')) return 'Add a username or password';
    if (types.includes('file')) return 'A login can’t hold a file';
  } else if (kind === 'file') {
    if (types.length !== 1 || types[0] !== 'file') return 'A file holds exactly one file';
  } else if (types.length !== 1 || types[0] !== 'link') {
    return 'A link holds exactly one link';
  }
  return null;
}

export const createDeliverableInputSchema = z
  .object({
    kind: deliverableKindSchema,
    ...deliverableBase,
    fields: z.array(deliverableFieldInputSchema).min(1, 'Add at least one field').max(20),
  })
  .superRefine((input, ctx) => {
    const message = deliverableShapeIssue(
      input.kind,
      input.fields.map((f) => f.type),
    );
    if (message) ctx.addIssue({ code: 'custom', path: ['fields'], message });
  });
export type CreateDeliverableInput = z.infer<typeof createDeliverableInputSchema>;

/** Editing replaces the field list: a field left out is removed, one with an `id` and no `value` keeps its secret. */
export const updateDeliverableInputSchema = z
  .object({
    ...deliverableBase,
    fields: z.array(deliverableFieldInputSchema).min(1).max(20),
  })
  .partial({ title: true, fields: true })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change');
export type UpdateDeliverableInput = z.infer<typeof updateDeliverableInputSchema>;

export const deliverableParamsSchema = z.object({ id: z.string().min(1).max(128) });
export const deliverableFieldParamsSchema = z.object({ id: z.string().min(1).max(128), fieldId: z.string().uuid() });
export const businessDeliverablesParamsSchema = z.object({ businessId: z.string().min(1).max(128) });

// --- What the client's vault shows ---------------------------------------------------------------------------------

const vaultItemBase = {
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  icon: deliverableIconSchema.nullable(),
};

/** A login in the list: who it is for, never what it contains. */
export const vaultLoginSchema = z.object(vaultItemBase);
export const vaultFileSchema = z.object({
  ...vaultItemBase,
  fieldId: z.string().uuid(),
  category: z.string().nullable(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export const vaultLinkSchema = z.object({ ...vaultItemBase, url: z.string() });

export const vaultSchema = z.object({
  logins: z.array(vaultLoginSchema),
  files: z.array(vaultFileSchema),
  links: z.array(vaultLinkSchema),
  storage: z.object({ usedBytes: z.number().nonnegative(), limitBytes: z.number().nonnegative() }),
});
export type Vault = z.infer<typeof vaultSchema>;
export type VaultLogin = z.infer<typeof vaultLoginSchema>;
export type VaultFile = z.infer<typeof vaultFileSchema>;
export type VaultLink = z.infer<typeof vaultLinkSchema>;

export const revealedFieldSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  type: fieldTypeSchema,
  value: z.string(),
});
/** What a reveal gives back: the values themselves, and how long the page may keep showing them. */
export const revealResultSchema = z.object({
  fields: z.array(revealedFieldSchema),
  hideAfterSeconds: z.number().int().positive(),
});
export type RevealResult = z.infer<typeof revealResultSchema>;

export const fileDownloadSchema = z.object({ url: z.string().url(), fileName: z.string() });

// --- What the team sees --------------------------------------------------------------------------------------------

export const staffFieldSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  type: fieldTypeSchema,
  secret: z.boolean(),
  visibleToClient: z.boolean(),
  /** Null for a secret: the team re-types a secret to change it, or reveals it (and that is logged). */
  value: z.string().nullable(),
  hasValue: z.boolean(),
  file: z.object({ name: z.string(), contentType: z.string(), size: z.number() }).nullable(),
});

export const staffDeliverableSchema = z.object({
  id: z.string(),
  kind: deliverableKindSchema,
  title: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  icon: deliverableIconSchema.nullable(),
  taskId: z.string().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Who added it, as "Arjun V." sees it in the list ("added by Arjun V. · 12 Oct"). */
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  fields: z.array(staffFieldSchema),
});
export type StaffDeliverable = z.infer<typeof staffDeliverableSchema>;

export const staffDeliverableListSchema = z.object({ items: z.array(staffDeliverableSchema) });

// --- Who opened a login (the team's "Reveal history") -------------------------------------------------------------

export const revealEntrySchema = z.object({
  id: z.string(),
  /** Null when the person's account has since been removed. */
  actorName: z.string().nullable(),
  /** "Admin", "Specialist", "Client": the role they had, as the list shows it in brackets. */
  actorRole: z.string(),
  by: z.enum(['staff', 'client']),
  at: z.string(),
  /** From the same browser as the one asking. */
  thisDevice: z.boolean(),
});
export type RevealEntry = z.infer<typeof revealEntrySchema>;

export const revealHistorySchema = z.object({ items: z.array(revealEntrySchema) });
export type RevealHistory = z.infer<typeof revealHistorySchema>;
